import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';

const DISMISS_KEY = 'da_newsletter_popup_dismissed_at';
const SUBSCRIBED_KEY = 'da_newsletter_popup_subscribed_at';
const SESSION_SHOWN_KEY = 'da_newsletter_popup_shown_session';
const VARIANT_KEY = 'da_newsletter_popup_variant';
const SESSION_ID_KEY = 'da_newsletter_popup_session_id';
const DISMISS_DAYS = 30;
const SUBSCRIBED_DAYS = 90;

const EXCLUDED_ROUTES = ['/auth', '/admin', '/contact', '/newsletter'];

// A/B test variants. Add more variants to expand the test; assignment is uniform.
type Variant = {
  id: string;
  title: string;
  description: string;
  cta: string;
  submittingCta: string;
};

const VARIANTS: Variant[] = [
  {
    id: 'A',
    title: 'Intelligence That Flies Higher',
    description:
      'Monthly insights on B2B intent data, signal science, and demand gen. No spam, unsubscribe anytime.',
    cta: 'Get Monthly Insights',
    submittingCta: 'Subscribing...',
  },
  {
    id: 'B',
    title: "Don't Miss the Next Buyer Signal",
    description:
      'Get the month\'s sharpest intent signals and demand gen plays delivered monthly. Free, unsubscribe anytime.',
    cta: 'Subscribe Free',
    submittingCta: 'Joining...',
  },
];

const emailSchema = z
  .string()
  .trim()
  .email({ message: 'Please enter a valid email address' })
  .max(255, { message: 'Email must be less than 255 characters' });

const isMobile = () =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches;

const shouldSuppress = (): boolean => {
  try {
    if (sessionStorage.getItem(SESSION_SHOWN_KEY)) return true;
    const subscribed = localStorage.getItem(SUBSCRIBED_KEY);
    if (subscribed && Date.now() - Number(subscribed) < SUBSCRIBED_DAYS * 86400000) return true;
    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed && Date.now() - Number(dismissed) < DISMISS_DAYS * 86400000) return true;
  } catch {
    /* storage unavailable */
  }
  return false;
};

const getOrAssignVariant = (): Variant => {
  try {
    const stored = localStorage.getItem(VARIANT_KEY);
    const found = VARIANTS.find((v) => v.id === stored);
    if (found) return found;
  } catch {
    /* ignore */
  }
  const pick = VARIANTS[Math.floor(Math.random() * VARIANTS.length)];
  try {
    localStorage.setItem(VARIANT_KEY, pick.id);
  } catch {
    /* ignore */
  }
  return pick;
};

const getSessionId = (): string => {
  try {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    return id;
  } catch {
    return `anon-${Date.now()}`;
  }
};

const logEvent = (
  variantId: string,
  eventType: 'impression' | 'conversion' | 'dismiss',
  pathname: string,
) => {
  // Fire-and-forget; never block UX on analytics.
  supabase
    .from('popup_ab_events')
    .insert({
      variant: variantId,
      event_type: eventType,
      session_id: getSessionId(),
      pathname,
    })
    .then(({ error }) => {
      if (error) console.warn('popup_ab_events insert failed:', error.message);
    });
};

export const NewsletterPopup = () => {
  const location = useLocation();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const convertedRef = useRef(false);

  const variant = useMemo(() => getOrAssignVariant(), []);
  const excluded = EXCLUDED_ROUTES.some((r) => location.pathname.startsWith(r));

  const openOnce = useCallback(() => {
    if (shouldSuppress()) return;
    try {
      sessionStorage.setItem(SESSION_SHOWN_KEY, '1');
    } catch {
      /* ignore */
    }
    setOpen(true);
    logEvent(variant.id, 'impression', location.pathname);
  }, [variant.id, location.pathname]);

  useEffect(() => {
    if (excluded) return;
    if (shouldSuppress()) return;

    const mobile = isMobile();
    const scrollThreshold = mobile ? 0.6 : 0.5;
    let fired = false;

    const onScroll = () => {
      if (fired) return;
      const doc = document.documentElement;
      const scrolled = (window.scrollY + window.innerHeight) / doc.scrollHeight;
      if (scrolled >= scrollThreshold) {
        fired = true;
        openOnce();
      }
    };

    const onMouseOut = (e: MouseEvent) => {
      if (fired) return;
      if (e.relatedTarget === null && e.clientY <= 0) {
        fired = true;
        openOnce();
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    if (!mobile) document.addEventListener('mouseout', onMouseOut);

    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseout', onMouseOut);
    };
  }, [excluded, openOnce, location.pathname]);

  const handleDismiss = (next: boolean) => {
    if (!next && open && !convertedRef.current) {
      try {
        localStorage.setItem(DISMISS_KEY, String(Date.now()));
      } catch {
        /* ignore */
      }
      logEvent(variant.id, 'dismiss', location.pathname);
    }
    setOpen(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = emailSchema.safeParse(email);
    if (!parsed.success) {
      toast({
        title: 'Invalid email',
        description: parsed.error.issues[0]?.message ?? 'Please enter a valid email address',
        variant: 'destructive',
      });
      return;
    }

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('newsletter-signup', {
        body: { email: parsed.data },
      });
      if (error) throw error;
      if (data && data.success === false) {
        toast({
          title: 'Already subscribed',
          description: data.error ?? 'This email is already on the list.',
        });
      } else {
        convertedRef.current = true;
        logEvent(variant.id, 'conversion', location.pathname);
        toast({
          title: 'You are on board',
          description: 'Check your inbox for a welcome email.',
        });
        try {
          localStorage.setItem(SUBSCRIBED_KEY, String(Date.now()));
        } catch {
          /* ignore */
        }
      }
      setEmail('');
      setOpen(false);
    } catch (err) {
      console.error('Newsletter popup signup error:', err);
      toast({
        title: 'Something went wrong',
        description: 'Please try again in a moment.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (excluded) return null;

  return (
    <Dialog open={open} onOpenChange={handleDismiss}>
      <DialogContent className="sm:max-w-md" data-ab-variant={variant.id}>
        <DialogHeader>
          <DialogTitle className="text-2xl">{variant.title}</DialogTitle>
          <DialogDescription>{variant.description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            required
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-label="Email address"
            disabled={submitting}
          />
          <Button type="submit" variant="cta" size="lg" disabled={submitting}>
            {submitting ? variant.submittingCta : variant.cta}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
